import * as THREE from 'three'
import type { TilesPickSource } from '@/shared/types/core'
import type { ViewId } from '@/shared/types/view'
import type { UnitPreference } from '@/shared/unitPresentation'

/** 测量类型 */
export type MeasureType =
  | 'coordinate'
  | 'distance'
  | 'height'
  | 'circle'
  | 'azimuth'
  | 'angle'
  | 'area'

/** 坐标测量采集模式 */
export type CoordinateMeasureMode = 'single' | 'continuous'

/** 测量点 */
export interface MeasurePoint {
  /** 点稳定标识；旧数据可缺省 */
  readonly id?: string
  /** 宿主提供的显示标签；缺省时由引擎按顺序生成 */
  readonly label?: string
  /** 世界坐标位置 */
  readonly position: THREE.Vector3
  /** 确认时的 Tiles 显示来源；不随资产变换或 LOD 重吸附。 */
  readonly tilesSource?: TilesPickSource
}

/** 根据实例偏好解析单次测量会话使用的局部单位偏好。 */
export type MeasureUnitPreferenceResolver = (
  globalPreference: Readonly<UnitPreference>
) => Partial<UnitPreference>

/** 测量工具会话配置 */
export interface MeasureToolSessionOptions {
  /** 初始测量类型 */
  measureType?: MeasureType
  /** 坐标测量采集模式；缺省为 single */
  coordinateMode?: CoordinateMeasureMode
  /** 是否将面积测量后续点约束到前三点平面；缺省为 true。 */
  areaPlaneConstraintEnabled?: boolean
  /** 本会话预览态及完成结果使用的局部单位偏好解析器。 */
  unitPreferenceResolver?: MeasureUnitPreferenceResolver
}

/** 当前测量草稿的业务快照 */
export interface MeasureDraftState {
  /** 当前测量类型 */
  readonly measureType: MeasureType
  /** 当前坐标测量采集模式 */
  readonly coordinateMode: CoordinateMeasureMode
  /** 草稿所属视口 */
  readonly viewId: ViewId | null
  /** 已确认的草稿点 */
  readonly points: ReadonlyArray<MeasurePoint>
  /** 当前草稿是否满足保存条件 */
  readonly canSave: boolean
}

/** 测量点移除的用户语义。 */
export type MeasurePointRemovalReason = 'undo' | 'remove'

/** 测量点移除事件。 */
export interface MeasurePointRemovalEvent {
  /** 操作语义：撤销最后一点或显式移除指定点。 */
  readonly reason: MeasurePointRemovalReason
  /** 被修改的是当前草稿还是已完成结果。 */
  readonly scope: 'draft' | 'result'
  /** 被移除点的稳定标识。 */
  readonly pointId: string
  /** 已完成结果 ID；草稿移除时缺省。 */
  readonly resultId?: string
  /** 移除后的剩余点数。 */
  readonly remainingPointCount: number
}

/** 高度测量结果 */
export interface HeightResult {
  /** 垂直距离 */
  readonly vertical: number
  /** 水平距离 */
  readonly horizontal: number
  /** 斜边与水平投影边的夹角，单位为度 */
  readonly slopeAngle: number
}

/** 外接圆测量结果 */
export interface CircleResult {
  /** 圆心 */
  readonly center: THREE.Vector3
  /** 半径 */
  readonly radius: number
  /** 周长 */
  readonly circumference: number
}

/** 测量结果 */
export interface MeasureResult {
  /** 唯一 ID */
  readonly id: string
  /** 所属视口；为空表示兼容旧数据，在所有视口可见 */
  readonly viewId?: ViewId
  /** 测量类型 */
  readonly measureType: MeasureType
  /** 测量点列表 */
  readonly points: ReadonlyArray<MeasurePoint>
  /** 面积边界对应的 points 索引；缺省时沿用 points 原顺序 */
  readonly areaBoundaryIndices?: ReadonlyArray<number>
  /** 当前面积结果是否启用平面约束；false 时使用 2.0 拟合面积展示。 */
  readonly areaPlaneConstraintEnabled?: boolean
  /** 总距离（米） */
  readonly distance: number
  /** 面积（仅 >=3 个点时有值，平方米） */
  readonly area: number | null
  /** XY 水平投影面积（兼容旧结果对象，可缺省） */
  readonly projectedArea?: number | null
  /** 角度（仅 3 个点时有值，度） */
  readonly angle: number | null
  /** XY 水平投影角度（兼容旧结果对象，可缺省） */
  readonly projectedAngle?: number | null
  /** 各线段长度 */
  readonly segmentDistances: ReadonlyArray<number>
  /** 高度测量（垂直/水平距离） */
  readonly height?: HeightResult
  /** 外接圆测量 */
  readonly circle?: CircleResult
  /** 方位角（从北方向顺时针，弧度） */
  readonly azimuth?: number
  /** 闭合路径总距离 */
  readonly closedDistance?: number
}

import { createIdGenerator } from '../shared/idGenerator'

/** 生成唯一测量 ID */
export const generateMeasureId = createIdGenerator('measure')

export interface BuildMeasureResultOptions {
  /** 指定结果 ID；恢复历史测量时使用 */
  id?: string
  /** 所属视口；为空表示兼容旧数据，在所有视口可见 */
  viewId?: ViewId | null
  /** 面积边界对应的 points 索引；非法时回退到 points 原顺序 */
  areaBoundaryIndices?: ReadonlyArray<number>
  /** 面积结果是否启用平面约束；false 时使用 2.0 拟合面积展示。 */
  areaPlaneConstraintEnabled?: boolean
}

type ProjectedAreaPoint = {
  x: number
  y: number
}

type SegmentRelation = 'none' | 'proper' | 'touch'

const AREA_BOUNDARY_POINT_EPSILON = 1e-9
const AREA_BOUNDARY_ORIENTATION_EPSILON = 1e-12

/** 2.0 面积逻辑使用的单一估算平面投影。 */
export interface AreaPlaneProjection {
  readonly normal: THREE.Vector3
  readonly projectedPoints: ReadonlyArray<THREE.Vector2>
  readonly planePoints: ReadonlyArray<THREE.Vector3>
  readonly area: number
}

function findFallbackAreaNormal(points: ReadonlyArray<MeasurePoint>): THREE.Vector3 | null {
  const firstDirection = new THREE.Vector3()
  const secondDirection = new THREE.Vector3()
  const normal = new THREE.Vector3()
  for (let i = 0; i < points.length - 2; i++) {
    for (let j = i + 1; j < points.length - 1; j++) {
      firstDirection.subVectors(points[j].position, points[i].position)
      for (let k = j + 1; k < points.length; k++) {
        secondDirection.subVectors(points[k].position, points[i].position)
        normal.crossVectors(firstDirection, secondDirection)
        if (normal.lengthSq() > AREA_BOUNDARY_ORIENTATION_EPSILON ** 2) {
          return normal.normalize().clone()
        }
      }
    }
  }
  return null
}

/** 按 2.0 逻辑将三维面积边界投影到由全部点估算的单一平面。 */
export function computeAreaPlaneProjection(
  points: ReadonlyArray<MeasurePoint>
): AreaPlaneProjection | null {
  if (points.length < 3) {
    return null
  }

  const normal = new THREE.Vector3()
  const firstDirection = new THREE.Vector3()
  const secondDirection = new THREE.Vector3()
  for (let i = 0; i < points.length; i++) {
    const next = (i + 1) % points.length
    const afterNext = (i + 2) % points.length
    firstDirection.subVectors(points[next].position, points[i].position)
    secondDirection.subVectors(points[afterNext].position, points[i].position)
    normal.add(new THREE.Vector3().crossVectors(firstDirection, secondDirection))
  }

  if (normal.lengthSq() > AREA_BOUNDARY_ORIENTATION_EPSILON ** 2) {
    normal.normalize()
  } else {
    const fallbackNormal = findFallbackAreaNormal(points)
    if (!fallbackNormal) {
      return null
    }
    normal.copy(fallbackNormal)
  }

  const origin = points[0].position
  let right = Math.abs(normal.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0)
  const forward = new THREE.Vector3().crossVectors(normal, right).normalize()
  right = new THREE.Vector3().crossVectors(forward, normal).normalize()
  const projectedPoints = points.map(({ position }) => {
    const delta = new THREE.Vector3().subVectors(position, origin)
    return new THREE.Vector2(delta.dot(right), delta.dot(forward))
  })
  const planePoints = projectedPoints.map(({ x, y }) =>
    origin.clone().addScaledVector(right, x).addScaledVector(forward, y)
  )

  let doubledArea = 0
  for (let i = 0; i < projectedPoints.length; i++) {
    const current = projectedPoints[i]
    const next = projectedPoints[(i + 1) % projectedPoints.length]
    doubledArea += current.x * next.y - next.x * current.y
  }

  return {
    normal: normal.clone(),
    projectedPoints,
    planePoints,
    area: Math.abs(doubledArea) * 0.5,
  }
}

function projectAreaPoints(points: ReadonlyArray<MeasurePoint>): ProjectedAreaPoint[] | null {
  if (points.length < 3) {
    return null
  }

  const origin = points[0].position
  const firstDirection = new THREE.Vector3()
  const secondDirection = new THREE.Vector3()
  const normal = new THREE.Vector3()
  let hasPlane = false

  for (let i = 1; i < points.length - 1 && !hasPlane; i += 1) {
    firstDirection.subVectors(points[i].position, origin)
    if (firstDirection.length() <= AREA_BOUNDARY_POINT_EPSILON) {
      continue
    }

    for (let j = i + 1; j < points.length; j += 1) {
      secondDirection.subVectors(points[j].position, origin)
      normal.crossVectors(firstDirection, secondDirection)
      if (normal.lengthSq() > AREA_BOUNDARY_ORIENTATION_EPSILON ** 2) {
        normal.normalize()
        hasPlane = true
        break
      }
    }
  }

  if (!hasPlane) {
    return null
  }

  const absX = Math.abs(normal.x)
  const absY = Math.abs(normal.y)
  const absZ = Math.abs(normal.z)
  return points.map(({ position }) => {
    const x = position.x - origin.x
    const y = position.y - origin.y
    const z = position.z - origin.z
    if (absX >= absY && absX >= absZ) {
      return { x: y, y: z }
    }
    if (absY >= absZ) {
      return { x, y: z }
    }
    return { x, y }
  })
}

function getOrientationEpsilon(points: ReadonlyArray<ProjectedAreaPoint>): number {
  let scale = 1
  for (const point of points) {
    scale = Math.max(scale, Math.abs(point.x), Math.abs(point.y))
  }
  return AREA_BOUNDARY_ORIENTATION_EPSILON * scale * scale
}

function orientation(
  first: ProjectedAreaPoint,
  second: ProjectedAreaPoint,
  third: ProjectedAreaPoint
): number {
  return (second.x - first.x) * (third.y - first.y) - (second.y - first.y) * (third.x - first.x)
}

function isPointOnSegment(
  point: ProjectedAreaPoint,
  start: ProjectedAreaPoint,
  end: ProjectedAreaPoint,
  epsilon: number
): boolean {
  return (
    point.x >= Math.min(start.x, end.x) - epsilon &&
    point.x <= Math.max(start.x, end.x) + epsilon &&
    point.y >= Math.min(start.y, end.y) - epsilon &&
    point.y <= Math.max(start.y, end.y) + epsilon
  )
}

function getSegmentRelation(
  firstStart: ProjectedAreaPoint,
  firstEnd: ProjectedAreaPoint,
  secondStart: ProjectedAreaPoint,
  secondEnd: ProjectedAreaPoint,
  epsilon: number
): SegmentRelation {
  const firstSideStart = orientation(firstStart, firstEnd, secondStart)
  const firstSideEnd = orientation(firstStart, firstEnd, secondEnd)
  const secondSideStart = orientation(secondStart, secondEnd, firstStart)
  const secondSideEnd = orientation(secondStart, secondEnd, firstEnd)

  if (
    ((firstSideStart > epsilon && firstSideEnd < -epsilon) ||
      (firstSideStart < -epsilon && firstSideEnd > epsilon)) &&
    ((secondSideStart > epsilon && secondSideEnd < -epsilon) ||
      (secondSideStart < -epsilon && secondSideEnd > epsilon))
  ) {
    return 'proper'
  }

  if (
    (Math.abs(firstSideStart) <= epsilon &&
      isPointOnSegment(secondStart, firstStart, firstEnd, epsilon)) ||
    (Math.abs(firstSideEnd) <= epsilon &&
      isPointOnSegment(secondEnd, firstStart, firstEnd, epsilon)) ||
    (Math.abs(secondSideStart) <= epsilon &&
      isPointOnSegment(firstStart, secondStart, secondEnd, epsilon)) ||
    (Math.abs(secondSideEnd) <= epsilon &&
      isPointOnSegment(firstEnd, secondStart, secondEnd, epsilon))
  ) {
    return 'touch'
  }

  return 'none'
}

function findBoundaryIntersection(
  order: ReadonlyArray<number>,
  projected: ReadonlyArray<ProjectedAreaPoint>,
  epsilon: number
): { firstEdge: number; secondEdge: number; relation: SegmentRelation } | null {
  for (let firstEdge = 0; firstEdge < order.length; firstEdge += 1) {
    const firstNext = (firstEdge + 1) % order.length
    for (let secondEdge = firstEdge + 1; secondEdge < order.length; secondEdge += 1) {
      const secondNext = (secondEdge + 1) % order.length
      if (firstNext === secondEdge || secondNext === firstEdge) {
        continue
      }

      const relation = getSegmentRelation(
        projected[order[firstEdge]],
        projected[order[firstNext]],
        projected[order[secondEdge]],
        projected[order[secondNext]],
        epsilon
      )
      if (relation !== 'none') {
        return { firstEdge, secondEdge, relation }
      }
    }
  }

  return null
}

/**
 * 为共面面积点生成确定性的无自交边界索引。
 *
 * 仅通过 2-opt 反转实际相交边之间的索引片段；已合法的凸、凹边界保持原顺序。
 * 重复点、非相邻接触或共线重叠等无法安全解交叉的输入返回 null。
 */
export function computeAreaBoundaryIndices(
  points: ReadonlyArray<MeasurePoint>,
  areaPlaneConstraintEnabled: boolean = true
): number[] | null {
  const projected = areaPlaneConstraintEnabled
    ? projectAreaPoints(points)
    : (computeAreaPlaneProjection(points)?.projectedPoints ?? null)
  if (!projected) {
    return null
  }

  const pointEpsilonSquared = AREA_BOUNDARY_POINT_EPSILON ** 2
  for (let i = 0; i < projected.length; i += 1) {
    for (let j = i + 1; j < projected.length; j += 1) {
      const dx = projected[i].x - projected[j].x
      const dy = projected[i].y - projected[j].y
      if (dx * dx + dy * dy <= pointEpsilonSquared) {
        return null
      }
    }
  }

  const order = points.map((_point, index) => index)
  const epsilon = getOrientationEpsilon(projected)
  const maxReversals = points.length ** 3
  for (let reversalCount = 0; reversalCount <= maxReversals; reversalCount += 1) {
    const intersection = findBoundaryIntersection(order, projected, epsilon)
    if (!intersection) {
      return order
    }
    if (intersection.relation !== 'proper' || reversalCount === maxReversals) {
      return null
    }

    const reversed = order.slice(intersection.firstEdge + 1, intersection.secondEdge + 1).reverse()
    order.splice(intersection.firstEdge + 1, reversed.length, ...reversed)
  }

  return null
}

function validateAreaBoundaryIndices(
  points: ReadonlyArray<MeasurePoint>,
  indices: ReadonlyArray<number> | undefined
): number[] | null {
  if (!indices || indices.length !== points.length) {
    return null
  }

  const unique = new Set<number>()
  for (const index of indices) {
    if (!Number.isInteger(index) || index < 0 || index >= points.length || unique.has(index)) {
      return null
    }
    unique.add(index)
  }
  return [...indices]
}

/** 解析面积结果的边界点；缺少或非法索引时兼容历史数据并沿用原点序。 */
export function resolveAreaBoundaryPoints(
  points: ReadonlyArray<MeasurePoint>,
  indices?: ReadonlyArray<number>
): ReadonlyArray<MeasurePoint> {
  const validIndices = validateAreaBoundaryIndices(points, indices)
  return validIndices ? validIndices.map((index) => points[index]) : points
}

/**
 * 计算相邻测量点之间的线段长度。
 *
 * @param points - 测量点有序列表
 * @returns 相邻两点距离数组，长度为 `points.length - 1`；少于 2 个点返回空数组
 */
export function computeSegmentDistances(points: ReadonlyArray<MeasurePoint>): number[] {
  const segments: number[] = []
  for (let i = 1; i < points.length; i++) {
    segments.push(points[i - 1].position.distanceTo(points[i].position))
  }
  return segments
}

/**
 * 计算所有线段长度之和。
 *
 * @param segments - 线段长度数组
 * @returns 累加总距离；空数组返回 0
 */
export function computeTotalDistance(segments: ReadonlyArray<number>): number {
  let total = 0
  for (let i = 0; i < segments.length; i++) {
    total += segments[i]
  }
  return total
}

/**
 * 计算多边形面积（>=3 个点时有值）。
 *
 * 使用 Newell 方法：对所有边做叉积累加，取模长的一半。
 *
 * @param points - 多边形顶点，按顺序排列
 * @returns 面积，单位平方米；不足 3 个点返回 null
 */
export function computeArea(points: ReadonlyArray<MeasurePoint>): number | null {
  if (points.length < 3) return null

  // Newell 方法求任意多边形面积
  const normal = new THREE.Vector3(0, 0, 0)
  const n = points.length

  for (let i = 0; i < n; i++) {
    const current = points[i].position
    const next = points[(i + 1) % n].position

    normal.x += (current.y - next.y) * (current.z + next.z)
    normal.y += (current.z - next.z) * (current.x + next.x)
    normal.z += (current.x - next.x) * (current.y + next.y)
  }

  return normal.length() * 0.5
}

/**
 * 计算多边形在 XY 水平面上的投影面积（>=3 个点时有值）。
 *
 * @param points - 多边形顶点，按顺序排列
 * @returns XY 投影面积，单位平方米；不足 3 个点返回 null
 */
export function computeProjectedArea(points: ReadonlyArray<MeasurePoint>): number | null {
  if (points.length < 3) return null

  const origin = points[0].position
  let doubledArea = 0
  for (let i = 0; i < points.length; i++) {
    const current = points[i].position
    const next = points[(i + 1) % points.length].position
    const currentX = current.x - origin.x
    const currentY = current.y - origin.y
    const nextX = next.x - origin.x
    const nextY = next.y - origin.y
    doubledArea += currentX * nextY - nextX * currentY
  }

  return Math.abs(doubledArea) * 0.5
}

/**
 * 计算三点夹角（仅 3 个点时有值，返回度数）。
 *
 * 计算 P0->P1 与 P2->P1 两向量的夹角。
 *
 * @param points - 测量点数组，长度必须为 3
 * @returns 夹角度数，范围 [0, 180]；点数不足或某边长度为 0 时返回 null 或 0
 */
export function computeAngle(points: ReadonlyArray<MeasurePoint>): number | null {
  if (points.length !== 3) return null

  const v1 = new THREE.Vector3().subVectors(points[0].position, points[1].position)
  const v2 = new THREE.Vector3().subVectors(points[2].position, points[1].position)

  const len1 = v1.length()
  const len2 = v2.length()
  if (len1 === 0 || len2 === 0) return 0

  // clamp 防止浮点误差导致 acos 参数越界
  const cosAngle = Math.max(-1, Math.min(1, v1.dot(v2) / (len1 * len2)))
  return THREE.MathUtils.radToDeg(Math.acos(cosAngle))
}

/**
 * 计算三点在 XY 水平面上的投影夹角（仅 3 个点时有值，返回度数）。
 *
 * @param points - 测量点数组，长度必须为 3
 * @returns XY 投影夹角，范围 [0, 180]；投影向量退化时返回 0
 */
export function computeProjectedAngle(points: ReadonlyArray<MeasurePoint>): number | null {
  if (points.length !== 3) return null

  const corner = points[1].position
  const v1x = points[0].position.x - corner.x
  const v1y = points[0].position.y - corner.y
  const v2x = points[2].position.x - corner.x
  const v2y = points[2].position.y - corner.y
  const len1 = Math.hypot(v1x, v1y)
  const len2 = Math.hypot(v2x, v2y)
  if (len1 === 0 || len2 === 0) return 0

  const cosAngle = Math.max(-1, Math.min(1, (v1x * v2x + v1y * v2y) / (len1 * len2)))
  return THREE.MathUtils.radToDeg(Math.acos(cosAngle))
}

/**
 * 计算两点之间的垂直距离和水平距离（Z-up 坐标系：Z=垂直，XY=水平面）。
 *
 * @param p1 - 起点世界坐标
 * @param p2 - 终点世界坐标
 * @returns 垂直距离与水平距离，单位均为米
 */
export function computeHeight(p1: THREE.Vector3, p2: THREE.Vector3): HeightResult {
  const vertical = Math.abs(p2.z - p1.z)
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  const horizontal = Math.sqrt(dx * dx + dy * dy)
  const slopeAngle = THREE.MathUtils.radToDeg(Math.atan2(vertical, horizontal))
  return { vertical, horizontal, slopeAngle }
}

/**
 * 计算三点外接圆（中垂线交点法）。
 *
 * @param p1 - 第一个点
 * @param p2 - 第二个点
 * @param p3 - 第三个点
 * @returns 外接圆（圆心、半径、周长）；三点共线或有重合点时返回 null
 */
export function computeCircle(
  p1: THREE.Vector3,
  p2: THREE.Vector3,
  p3: THREE.Vector3
): CircleResult | null {
  const AB = new THREE.Vector3().subVectors(p2, p1)
  const AC = new THREE.Vector3().subVectors(p3, p1)

  // 法向量：若叉积为零向量则三点共线
  const N = new THREE.Vector3().crossVectors(AC, AB)
  if (N.lengthSq() < 1e-10) return null
  N.normalize()

  // AB/AC 中垂线方向（边向量叉法向量）
  const abDir = new THREE.Vector3().crossVectors(AB, N).normalize()
  const acDir = new THREE.Vector3().crossVectors(AC, N).normalize()

  // AB/AC 中点
  const abMid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5)
  const acMid = new THREE.Vector3().addVectors(p1, p3).multiplyScalar(0.5)

  // 求两中垂线交点（最近点法）
  const center = lineLineIntersection(abMid, abDir, acMid, acDir)
  if (!center) return null

  const radius = center.distanceTo(p1)
  const circumference = 2 * Math.PI * radius
  return { center, radius, circumference }
}

/**
 * 求两条参数直线的最近交点。
 * P = origin1 + t * dir1, Q = origin2 + s * dir2
 * 返回最近点的中点，若平行则返回 null。
 */
function lineLineIntersection(
  origin1: THREE.Vector3,
  dir1: THREE.Vector3,
  origin2: THREE.Vector3,
  dir2: THREE.Vector3
): THREE.Vector3 | null {
  const d = new THREE.Vector3().subVectors(origin2, origin1)
  const cross = new THREE.Vector3().crossVectors(dir1, dir2)
  const denom = cross.lengthSq()
  if (denom < 1e-10) return null

  const t = new THREE.Vector3().crossVectors(d, dir2).dot(cross) / denom
  return origin1.clone().addScaledVector(dir1, t)
}

/**
 * 计算方位角（从北方向顺时针）。
 *
 * Z-up 坐标系下水平面为 XY，Y+ 为北，X+ 为东。
 *
 * @param p1 - 起点
 * @param p2 - 终点
 * @returns 方位角弧度：0=北, PI/2=东, PI=南, -PI/2=西
 */
export function computeAzimuth(p1: THREE.Vector3, p2: THREE.Vector3): number {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  // atan2(dx, dy)：dy 在前（北方向），dx 在后（东方向）
  return Math.atan2(dx, dy)
}

/**
 * 计算闭合路径总距离（开放路径距离 + 首尾闭合段）。
 *
 * @param segmentDistances - 开放路径各段长度
 * @param firstPoint - 第一个点，用于闭合段
 * @param lastPoint - 最后一个点，用于闭合段
 * @returns 闭合路径总距离，单位米
 */
export function computeClosedPathDistance(
  segmentDistances: ReadonlyArray<number>,
  firstPoint: THREE.Vector3,
  lastPoint: THREE.Vector3
): number {
  let total = 0
  for (let i = 0; i < segmentDistances.length; i++) {
    total += segmentDistances[i]
  }
  total += firstPoint.distanceTo(lastPoint)
  return total
}

/**
 * 根据测量点列表和类型构建完整的测量结果。
 *
 * @param points - 测量点列表
 * @param measureType - 测量类型，默认为 distance
 * @returns 测量结果，按类型附带对应的衍生字段（height/circle/azimuth 等）
 */
export function buildMeasureResult(
  points: ReadonlyArray<MeasurePoint>,
  measureType: MeasureType = 'distance',
  options: BuildMeasureResultOptions = {}
): MeasureResult {
  const segmentDistances = computeSegmentDistances(points)
  const distance = computeTotalDistance(segmentDistances)
  const validAreaBoundaryIndices =
    measureType === 'area' ? validateAreaBoundaryIndices(points, options.areaBoundaryIndices) : null
  const areaPoints = validAreaBoundaryIndices
    ? validAreaBoundaryIndices.map((index) => points[index])
    : points
  const area =
    measureType === 'area' && options.areaPlaneConstraintEnabled === false
      ? (computeAreaPlaneProjection(areaPoints)?.area ?? null)
      : computeArea(areaPoints)
  const projectedArea = computeProjectedArea(areaPoints)
  const angle = computeAngle(points)
  const projectedAngle = computeProjectedAngle(points)

  const result: MeasureResult = {
    id: options.id ?? generateMeasureId(),
    ...(options.viewId ? { viewId: options.viewId } : {}),
    measureType,
    points: [...points],
    ...(validAreaBoundaryIndices ? { areaBoundaryIndices: validAreaBoundaryIndices } : {}),
    ...(measureType === 'area' && options.areaPlaneConstraintEnabled !== undefined
      ? { areaPlaneConstraintEnabled: options.areaPlaneConstraintEnabled }
      : {}),
    distance,
    area,
    projectedArea,
    angle,
    projectedAngle,
    segmentDistances,
  }

  if (measureType === 'height' && points.length >= 2) {
    const p0 = points[0].position
    const p1 = points[points.length - 1].position
    return { ...result, height: computeHeight(p0, p1) }
  }

  if (measureType === 'circle' && points.length === 3) {
    const circleResult = computeCircle(points[0].position, points[1].position, points[2].position)
    return circleResult ? { ...result, circle: circleResult } : result
  }

  if (measureType === 'azimuth' && points.length >= 2) {
    const az = computeAzimuth(points[0].position, points[points.length - 1].position)
    return { ...result, azimuth: az }
  }

  if (measureType === 'area' && points.length >= 3) {
    const boundarySegmentDistances = computeSegmentDistances(areaPoints)
    const closed = computeClosedPathDistance(
      boundarySegmentDistances,
      areaPoints[0].position,
      areaPoints[areaPoints.length - 1].position
    )
    return { ...result, closedDistance: closed }
  }

  return result
}
